import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { disputesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  FileText, Plus, Clock, CheckCircle, AlertCircle, Send, 
  Building2, Loader2, Download, Lightbulb, Zap, TrendingUp,
  AlertTriangle, Target
} from 'lucide-react';
import { toast } from 'sonner';

export default function DisputesPage() {
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [creditItems, setCreditItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [newItem, setNewItem] = useState({
    account_name: '',
    account_type: 'credit_card',
    balance: 0,
    status: 'Late Payment',
    date_reported: '',
    bureau: 'Experian',
    is_negative: true
  });

  useEffect(function loadData() {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const disputesData = await disputesApi.getAll();
      setDisputes(disputesData.disputes || []);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
    }
    try {
      const itemsData = await disputesApi.getCreditItems();
      setCreditItems(itemsData.items || []);
    } catch (err) {
      console.error('Failed to fetch credit items:', err);
    }
    setLoading(false);
  }

  async function fetchRecommendations() {
    setLoadingRecs(true);
    try {
      const data = await disputesApi.getInstantRecommendations();
      setRecommendations(data);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      toast.error('Failed to get recommendations');
    } finally {
      setLoadingRecs(false);
    }
  }

  const handleAddItem = useCallback(async function() {
    try {
      await disputesApi.addCreditItems([newItem]);
      toast.success('Credit item added');
      setAddItemOpen(false);
      setNewItem({
        account_name: '',
        account_type: 'credit_card',
        balance: 0,
        status: 'Late Payment',
        date_reported: '',
        bureau: 'Experian',
        is_negative: true
      });
      await fetchData();
      // Auto-fetch recommendations after adding item
      fetchRecommendations();
    } catch (err) {
      toast.error('Failed to add item');
    }
  }, [newItem]);

  const handleAnalyze = useCallback(async function() {
    setAnalyzing(true);
    try {
      await disputesApi.analyze();
      toast.success('Analysis complete');
      fetchRecommendations();
    } catch (err) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const handleGenerateDisputes = useCallback(async function() {
    if (!isPremium) {
      toast.error('Premium subscription required');
      return;
    }
    if (!user || !user.is_authorized) {
      toast.error('Please complete authorization first');
      return;
    }
    
    setGenerating(true);
    try {
      await disputesApi.generate();
      toast.success('Disputes generated and sent!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate disputes');
    } finally {
      setGenerating(false);
    }
  }, [isPremium, user]);

  const handleDownloadSummary = useCallback(async function() {
    setDownloadingPdf(true);
    try {
      const blob = await disputesApi.downloadSummaryPdf();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit_summary_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  }, []);

  const handleDownloadLetter = useCallback(async function(disputeId, trackingNumber) {
    try {
      const blob = await disputesApi.downloadLetterPdf(disputeId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dispute_letter_${trackingNumber || disputeId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Letter downloaded');
    } catch (err) {
      toast.error('Failed to download letter');
    }
  }, []);

  function getStatusColor(status) {
    if (status === 'draft') return 'bg-slate-100 text-slate-700';
    if (status === 'approved_pending_mail') return 'bg-teal-100 text-teal-700';
    if (status === 'sent') return 'bg-blue-100 text-blue-700';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (status === 'in_review') return 'bg-purple-100 text-purple-700';
    if (status === 'resolved') return 'bg-green-100 text-green-700';
    return 'bg-slate-100 text-slate-700';
  }

  function getStatusLabel(status) {
    if (status === 'draft') return 'Draft';
    if (status === 'approved_pending_mail') return 'Queued to Mail';
    if (status === 'sent') return 'Sent';
    if (status === 'pending') return 'Pending';
    if (status === 'in_review') return 'In Review';
    if (status === 'resolved') return 'Resolved';
    return status || 'Unknown';
  }

  function getImpactColor(impact) {
    if (impact === 'high') return 'text-red-600 bg-red-50';
    if (impact === 'medium') return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  }

  const activeDisputes = disputes.filter(function(d) {
    return ['draft', 'approved_pending_mail', 'sent', 'pending', 'in_review'].includes(d.status);
  });
  const completedDisputes = disputes.filter(function(d) {
    return d.status === 'resolved';
  });
  const negativeItems = creditItems.filter(function(i) {
    return i.is_negative && !i.disputed;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/4" />
        <div className="h-48 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="disputes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Disputes
          </h1>
          <p className="text-slate-600 mt-1">Manage your credit disputes and track progress.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={handleDownloadSummary}
            disabled={downloadingPdf || creditItems.length === 0}
            data-testid="download-summary-btn"
          >
            {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download Report
          </Button>
          <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="add-item-btn">
                <Plus className="h-4 w-4" />
                Add Credit Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="add-item-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Credit Report Item</DialogTitle>
                <DialogDescription>
                  Enter details from your credit report to track and dispute.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Account Name</Label>
                  <Input
                    placeholder="e.g., Chase Credit Card"
                    value={newItem.account_name}
                    onChange={function(e) { setNewItem(function(prev) { return { ...prev, account_name: e.target.value }; }); }}
                    data-testid="item-account-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account Type</Label>
                    <Select 
                      value={newItem.account_type}
                      onValueChange={function(value) { setNewItem(function(prev) { return { ...prev, account_type: value }; }); }}
                    >
                      <SelectTrigger data-testid="item-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="loan">Loan</SelectItem>
                        <SelectItem value="mortgage">Mortgage</SelectItem>
                        <SelectItem value="collection">Collection</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bureau</Label>
                    <Select 
                      value={newItem.bureau}
                      onValueChange={function(value) { setNewItem(function(prev) { return { ...prev, bureau: value }; }); }}
                    >
                      <SelectTrigger data-testid="item-bureau-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Experian">Experian</SelectItem>
                        <SelectItem value="Equifax">Equifax</SelectItem>
                        <SelectItem value="TransUnion">TransUnion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Balance</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={newItem.balance}
                      onChange={function(e) { setNewItem(function(prev) { return { ...prev, balance: parseFloat(e.target.value) || 0 }; }); }}
                      data-testid="item-balance"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select 
                      value={newItem.status}
                      onValueChange={function(value) { setNewItem(function(prev) { return { ...prev, status: value }; }); }}
                    >
                      <SelectTrigger data-testid="item-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Late Payment">Late Payment</SelectItem>
                        <SelectItem value="Collection">Collection</SelectItem>
                        <SelectItem value="Charge Off">Charge Off</SelectItem>
                        <SelectItem value="Account Not Mine">Account Not Mine</SelectItem>
                        <SelectItem value="Incorrect Balance">Incorrect Balance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Date Reported</Label>
                  <Input
                    type="date"
                    value={newItem.date_reported}
                    onChange={function(e) { setNewItem(function(prev) { return { ...prev, date_reported: e.target.value }; }); }}
                    data-testid="item-date"
                  />
                </div>
                <Button 
                  className="w-full btn-teal"
                  onClick={handleAddItem}
                  disabled={!newItem.account_name}
                  data-testid="save-item-btn"
                >
                  Add Item
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {isPremium && user && user.is_authorized && (
            <Button 
              className="btn-teal gap-2"
              onClick={handleGenerateDisputes}
              disabled={generating || negativeItems.length === 0}
              data-testid="generate-disputes-btn"
            >
              {generating ? (
                <span className="flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</span>
              ) : (
                <span className="flex items-center"><Send className="h-4 w-4 mr-2" /> Send Disputes</span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Instant Recommendations Section */}
      <Card className="border-teal-200 bg-gradient-to-r from-teal-50/50 to-white" data-testid="recommendations-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-teal-600" />
              <CardTitle style={{ fontFamily: 'Manrope' }}>Instant AI Recommendations</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchRecommendations}
              disabled={loadingRecs || creditItems.length === 0}
              data-testid="refresh-recs-btn"
            >
              {loadingRecs ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
          <CardDescription>
            Personalized insights based on your credit report items
          </CardDescription>
        </CardHeader>
        <CardContent>
          {creditItems.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <Target className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p>Add credit items above to receive instant recommendations.</p>
            </div>
          ) : loadingRecs ? (
            <div className="text-center py-6">
              <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-teal-600" />
              <p className="text-slate-600">Analyzing your credit profile...</p>
            </div>
          ) : recommendations ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-slate-700">{recommendations.summary}</p>
              </div>

              {/* Score Factors */}
              {recommendations.score_factors && recommendations.score_factors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-teal-600" />
                    What's Affecting Your Score
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {recommendations.score_factors.map(function(factor, index) {
                      return (
                        <div key={index} className={`rounded-xl p-4 border ${getImpactColor(factor.impact)}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{factor.factor}</span>
                            <Badge variant="outline" className={getImpactColor(factor.impact)}>
                              {factor.impact}
                            </Badge>
                          </div>
                          <p className="text-sm opacity-80">{factor.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {recommendations.recommendations && recommendations.recommendations.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-teal-600" />
                    Recommended Actions
                  </h4>
                  <div className="space-y-3">
                    {recommendations.recommendations.map(function(rec, index) {
                      return (
                        <div key={index} className="bg-white rounded-xl p-4 border border-slate-200 flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold shrink-0">
                            {rec.priority || index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{rec.action}</p>
                            <p className="text-sm text-slate-600 mt-1">{rec.reason}</p>
                            {rec.timeline && (
                              <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {rec.timeline}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick Wins */}
              {recommendations.quick_wins && recommendations.quick_wins.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Quick Wins
                  </h4>
                  <ul className="space-y-2">
                    {recommendations.quick_wins.map(function(win, index) {
                      return (
                        <li key={index} className="flex items-start gap-2 text-sm text-amber-700">
                          <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                          {win}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <Button onClick={fetchRecommendations} className="btn-teal" data-testid="get-recs-btn">
                Get My Recommendations
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credit Items Section */}
      <Card className="border-slate-100 shadow-sm" data-testid="credit-items-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Credit Report Items</CardTitle>
              <CardDescription>Items from your credit report that may be disputable</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleAnalyze}
              disabled={analyzing || creditItems.length === 0}
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <span className="flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</span>
              ) : (
                'AI Analyze'
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {creditItems.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No credit items added yet.</p>
              <p className="text-sm mt-1">Add items from your credit report to start disputing.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {creditItems.map(function(item) {
                return (
                  <div 
                    key={item.item_id}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      item.disputed ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                    }`}
                    data-testid={'credit-item-' + item.item_id}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        item.is_negative ? 'bg-red-100' : 'bg-green-100'
                      }`}>
                        <Building2 className={`h-5 w-5 ${item.is_negative ? 'text-red-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{item.account_name}</p>
                        <p className="text-sm text-slate-500">
                          {item.account_type} • {item.bureau} • ${(item.balance || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={item.is_negative ? 'destructive' : 'secondary'}>
                        {item.status}
                      </Badge>
                      {item.disputed && (
                        <Badge className="bg-blue-100 text-blue-700">Disputed</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disputes Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList data-testid="disputes-tabs">
          <TabsTrigger value="active" data-testid="active-tab">
            Active ({activeDisputes.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="completed-tab">
            Completed ({completedDisputes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="pt-6">
              {activeDisputes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No active disputes.</p>
                  {!isPremium && (
                    <p className="text-sm mt-1">Upgrade to Premium for automatic dispute sending.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {activeDisputes.map(function(dispute) {
                    return (
                      <div 
                        key={dispute.dispute_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        data-testid={'active-dispute-' + dispute.dispute_id}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{dispute.bureau}</p>
                            <p className="text-sm text-slate-500">
                              {dispute.items ? dispute.items.length : 0} {(dispute.items && dispute.items.length === 1) ? 'item' : 'items'}
                              {(function() {
                                if (dispute.sent_date) return ' • Sent ' + new Date(dispute.sent_date).toLocaleDateString();
                                if (dispute.approved_at) return ' • Queued ' + new Date(dispute.approved_at).toLocaleDateString();
                                return ' • Created ' + new Date(dispute.created_at).toLocaleDateString();
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {dispute.mail_class === 'certified' && dispute.certified_paid_at && (
                            <Badge className="bg-amber-100 text-amber-700 whitespace-nowrap">
                              Certified · $5.50
                            </Badge>
                          )}
                          <Badge className={getStatusColor(dispute.status)}>
                            {getStatusLabel(dispute.status)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={function() { handleDownloadLetter(dispute.dispute_id, dispute.tracking_number); }}
                            data-testid={'download-letter-' + dispute.dispute_id}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="pt-6">
              {completedDisputes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No completed disputes yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedDisputes.map(function(dispute) {
                    return (
                      <div 
                        key={dispute.dispute_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        data-testid={'completed-dispute-' + dispute.dispute_id}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{dispute.bureau}</p>
                            <p className="text-sm text-slate-500">
                              {dispute.items ? dispute.items.length : 0} items • {dispute.resolution || 'Resolved'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-green-100 text-green-700">
                            {dispute.resolution || 'Resolved'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={function() { handleDownloadLetter(dispute.dispute_id, dispute.tracking_number); }}
                            data-testid={'download-letter-' + dispute.dispute_id}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
